import classNames from 'classnames';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SelectOption } from './useSelect';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export type SelectVariant = 'field' | 'pill';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: SelectOption[];
  className?: string;
  optionClassName?: string;
  handleSelect: (option: any) => void;
  /** Visual treatment of the trigger. `pill` matches the design-file filter pills. */
  variant?: SelectVariant;
  /** Text shown before the selected label, e.g. `Status` → "Status: all". */
  labelPrefix?: string;
  /**
   * Force the native <select>, which opens the OS picker. Left undefined it
   * follows the pointer: coarse (touch) gets native, fine (mouse) gets the
   * custom menu. Either way the trigger looks the same.
   */
  native?: boolean;
  /** @deprecated the custom dropdown is now the default — use `native` to opt out. */
  asButton?: boolean;
  buttonLabel?: string; // Placeholder label when nothing is selected
  buttonClassName?: string; // Custom className for the trigger
  /** Value from `useSelect`, which returns `selectedValue` rather than `value`. */
  selectedValue?: any;
}

const MENU_MARGIN = 6;
const COARSE_POINTER = '(pointer: coarse)';

/** True on touch-primary devices, where the OS picker beats a custom menu. */
function useCoarsePointer() {
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(COARSE_POINTER).matches === true,
  );

  useEffect(() => {
    const query = window.matchMedia?.(COARSE_POINTER);
    if (!query) return;

    const onChange = (e: MediaQueryListEvent) => setCoarse(e.matches);
    setCoarse(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return coarse;
}

const Select: React.FC<SelectProps> = ({
  options,
  optionClassName,
  className = '',
  variant = 'field',
  labelPrefix,
  native,
  asButton,
  buttonLabel,
  buttonClassName,
  handleSelect,
  selectedValue,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const coarsePointer = useCoarsePointer();
  const useNative = native ?? coarsePointer;

  // `useSelect` returns helpers that are not valid DOM attributes — spreading its
  // result onto <Select> must not leak them onto the underlying element.
  const {
    setIsOpen: _setIsOpen,
    setHighlightedIndex: _setHighlightedIndex,
    highlightedIndex: _highlightedIndex,
    isOpen: _isOpen,
    selectRef: _selectRef,
    handleToggle: _handleToggle,
    handleKeyDown: _handleKeyDown,
    ...selectProps
  } = props as any;

  const value = props.value !== undefined ? props.value : selectedValue;
  const selectedIndex = useMemo(
    // eslint-disable-next-line eqeqeq
    () => options.findIndex(option => option.value == value),
    [options, value],
  );
  const disabled = !options?.length || !!props.disabled;

  // Position the portalled menu under (or above, when short on room) the trigger.
  useEffect(() => {
    if (!isOpen || useNative) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom;
      const flip = below < 200 && rect.top > below;

      setMenuStyle(
        flip
          ? {
              bottom: window.innerHeight - rect.top + MENU_MARGIN,
              left: rect.left,
              minWidth: rect.width,
              maxHeight: Math.min(rect.top - MENU_MARGIN * 2, 288),
            }
          : {
              top: rect.bottom + MENU_MARGIN,
              left: rect.left,
              minWidth: rect.width,
              maxHeight: Math.min(below - MENU_MARGIN * 2, 288),
            },
      );
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, useNative]);

  // Close when clicking outside the trigger or the menu
  useEffect(() => {
    if (!isOpen || useNative) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, useNative]);

  // A pointer switch (tablet keyboard attach/detach) must not strand an open menu.
  useEffect(() => {
    if (useNative) setIsOpen(false);
  }, [useNative]);

  const open = useCallback(() => {
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : options.findIndex(option => !option.disabled));
    setIsOpen(true);
  }, [options, selectedIndex]);

  const choose = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;
      handleSelect(option);
      setIsOpen(false);
      buttonRef.current?.focus();
    },
    [handleSelect],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
        return;
      }

      const step = (delta: number) => {
        e.preventDefault();
        let next = highlightedIndex;
        for (let i = 0; i < options.length; i++) {
          next = (next + delta + options.length) % options.length;
          if (!options[next]?.disabled) break;
        }
        setHighlightedIndex(next);
      };

      if (e.key === 'ArrowDown') step(1);
      else if (e.key === 'ArrowUp') step(-1);
      else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        setHighlightedIndex(e.key === 'Home' ? 0 : options.length - 1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const option = options[highlightedIndex];
        if (option) choose(option);
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        setIsOpen(false);
      }
    },
    [choose, highlightedIndex, isOpen, open, options],
  );

  const isPill = variant === 'pill';
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const displayLabel = selectedOption?.label || buttonLabel || 'Select…';

  // Shared by both branches so the closed state is identical on touch and desktop.
  const triggerClassName = classNames(
    'inline-flex w-full items-center justify-between gap-2 bg-white transition-colors',
    'disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-adm-accent/40',
    'dark:bg-howio-900 dark:text-white',
    isPill
      ? 'h-[30px] rounded-full border border-adm-line px-3 text-xs text-adm-ink2 enabled:hover:border-adm-ink3 enabled:hover:text-adm-ink dark:border-howio-600'
      : 'rounded-lg border border-adm-line px-3 py-2 text-sm text-adm-ink shadow-sm enabled:hover:border-adm-ink3 dark:border-howio-600',
    isOpen && 'border-adm-accent text-adm-accent',
    buttonClassName,
  );

  const triggerContent = (
    <>
      <span className="truncate">
        {labelPrefix ? <span className="text-adm-ink3">{labelPrefix}: </span> : null}
        {displayLabel}
      </span>
      <ChevronDownIcon
        className={classNames(
          'shrink-0 text-adm-ink3 transition-transform',
          isPill ? 'h-3.5 w-3.5' : 'h-4 w-4',
          isOpen && 'rotate-180',
        )}
      />
    </>
  );

  // Touch: a real <select> laid transparently over the styled trigger, so the tap
  // opens the OS picker (wheel on iOS, dialog on Android) with our chrome visible.
  if (useNative) {
    return (
      <div className={classNames(className, 'inline-block')}>
        <div className={classNames('relative', disabled && 'opacity-50')}>
          {/* Ordered select-then-visual so `peer-focus-visible` can mirror focus onto the trigger. */}
          <select
            {...selectProps}
            value={value ?? ''}
            disabled={disabled}
            className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
            onChange={e => {
              // eslint-disable-next-line eqeqeq
              const option = options.find(o => o.value == e.target.value);

              if (option) {
                handleSelect(option);
              }
            }}
          >
            {options.map(option => (
              <option key={option.value} value={option.value} disabled={option.disabled} className={optionClassName}>
                {option.label}
              </option>
            ))}
          </select>
          <div
            aria-hidden
            className={classNames(
              triggerClassName,
              'pointer-events-none relative peer-focus-visible:ring-2 peer-focus-visible:ring-adm-accent/40',
            )}
          >
            {triggerContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(className, 'inline-block')}>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          id={selectProps.id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
          onClick={() => (isOpen ? setIsOpen(false) : open())}
          onKeyDown={onKeyDown}
          className={triggerClassName}
        >
          {triggerContent}
        </button>

        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              role="listbox"
              className={classNames(
                'fixed z-[99999] overflow-auto rounded-xl border border-adm-line bg-white p-1',
                'shadow-[0_10px_30px_rgba(17,17,17,0.10)] dark:border-howio-600 dark:bg-howio-900',
              )}
              style={menuStyle}
            >
              {options.map((option, index) => {
                const isSelected = index === selectedIndex;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => choose(option)}
                    className={classNames(
                      optionClassName,
                      'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm',
                      'disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none',
                      index === highlightedIndex && !option.disabled && 'bg-adm-surface2 dark:bg-howio-700',
                      isSelected
                        ? 'font-medium text-adm-accent'
                        : 'text-adm-ink dark:text-white dark:enabled:hover:text-white',
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <CheckIcon className="h-4 w-4 shrink-0" />}
                  </button>
                );
              })}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default Select;
